import type React from 'react';
import {useState} from 'react';
import {Alert, Box, Button, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {confidenceLabel, factorValues, ordinal, overrideText} from '../../domain/riskScore';
import {useRiskActions} from '../../hooks/useRiskActions';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';
import {riskTag} from '../../explore/exploreColumns';
import type {RiskScore} from '../../types/categoryProfileTypes';
import {ProfileSection} from '../components/ProfileSection';
import {RiskOverrideDialog} from './RiskOverrideDialog';

const Bar: React.FC<{label: string; value: number | null}> = ({label, value}) => (
    <Box sx={{minWidth: 0, flex: 1}}>
        <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="caption" sx={{fontWeight: 600}}>{value === null ? '—' : `${ordinal(value)} percentile`}</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={value ?? 0} sx={{height: 6, borderRadius: 3, mt: 0.5}}
                        color={value !== null && value >= 66.7 ? 'error' : value !== null && value >= 33.3 ? 'warning' : 'success'}/>
    </Box>
);

type Props = {score: RiskScore | null | undefined; databagId: number; path: string; canEdit: boolean};

/**
 * The category's risk score (Risk scoring settings): its level, supply risk and impact as percentiles against the
 * other categories at its level, and each factor, used (value, percentile, share of the score) or not (why). Category
 * editors set a level by hand or leave factors out, with a reason; a refresh that changes the calculation is shown.
 */

export const RiskScoreSection: React.FC<Props> = ({score, databagId, path, canEdit}) => {
    const actions = useRiskActions(databagId, path);
    const [editing, setEditing] = useState(false);
    const team = score ? overrideText(score) : null;
    return (
    <ProfileSection title="Risk score" help={OVERVIEW_HELP.riskScore}
                    subtitle={score && score.calculatedLevel !== 'insufficient' && score.level !== 'insufficient'
                        ? `${confidenceLabel(score.confidence)}: based on ${score.weightUsedPct}% of the configured weight` : undefined}
                    actions={canEdit && score && (
                        <Stack direction="row" gap={1}>
                            {score.override && <Button size="small" onClick={() => void actions.remove()}>Back to calculated</Button>}
                            <Button size="small" onClick={() => setEditing(true)}>{score.override ? 'Change…' : 'Change risk…'}</Button>
                        </Stack>
                    )}>
        {score?.notice && (
            <Alert severity="warning" sx={{mb: 1.5}} action={canEdit && (
                <Stack direction="row" gap={1}>
                    <Button size="small" color="inherit" onClick={() => void actions.keep()}>Keep the team&apos;s level</Button>
                    <Button size="small" color="inherit" onClick={() => void actions.remove()}>Back to calculated</Button>
                </Stack>
            )}>{score.notice}</Alert>
        )}
        {team && <Alert severity="info" sx={{mb: 1.5}}>{team}</Alert>}
        {editing && score && <RiskOverrideDialog score={score} onSubmit={actions.set} onClose={() => setEditing(false)}/>}
        {!score ? (
            <Typography variant="body2" color="text.secondary">The risk score appears after the next category build.</Typography>
        ) : score.level === 'insufficient' || score.calculatedLevel === 'insufficient' ? (
            <Stack direction="row" gap={1.5} alignItems="center">
                {riskTag(score.level, undefined, score)}
                <Typography variant="body2" color="text.secondary">{score.reason}</Typography>
            </Stack>
        ) : (
            <Stack gap={2}>
                <Stack direction={{xs: 'column', sm: 'row'}} gap={3} alignItems={{sm: 'center'}}>
                    {riskTag(score.level, undefined, score)}
                    <Bar label="Supply risk" value={score.supplyRisk}/>
                    <Bar label="Impact (spend)" value={score.impact}/>
                </Stack>
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small" aria-label="Risk factors">
                        <TableHead>
                            <TableRow>
                                <TableCell>Factor</TableCell>
                                <TableCell>Value</TableCell>
                                <TableCell align="right">Percentile</TableCell>
                                <TableCell align="right">Share of score</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {score.factors.map(f => (
                                <TableRow key={f.key} sx={f.used ? undefined : {'& td': {color: 'text.secondary'}}}>
                                    <TableCell sx={{fontWeight: f.used ? 600 : 400, whiteSpace: 'nowrap'}}>{f.label}</TableCell>
                                    <TableCell>{f.used ? factorValues(f) || '—' : `Not used: ${f.reason}`}</TableCell>
                                    <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>{f.used ? ordinal(f.percentile ?? 0) : '—'}</TableCell>
                                    <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>{f.used ? `${f.weightPct}%` : '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Stack>
        )}
    </ProfileSection>
    );
};
